import { useCallback, useEffect, useRef, useState } from 'react'
import { Posicao } from './geo'

export type SituacaoLocalizacao = 'inicial' | 'pedindo' | 'ok' | 'negada' | 'indisponivel' | 'erro'

/**
 * A posição da pessoa, pedida ao navegador. Só pede quando a tela manda (o botão "Usar minha localização"), exceto se a pessoa
 * já liberou antes neste site: aí abre direto, sem repetir a pergunta. A posição fica neste estado e não vai a lugar nenhum.
 */
export function useLocalizacao() {
  const [situacao, setSituacao] = useState<SituacaoLocalizacao>('inicial')
  const [posicao, setPosicao] = useState<Posicao | null>(null)
  const vivo = useRef(true)
  useEffect(() => () => { vivo.current = false }, [])

  const pedir = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { setSituacao('indisponivel'); return }
    setSituacao('pedindo')
    navigator.geolocation.getCurrentPosition(
      p => {
        if (!vivo.current) return
        setPosicao({ lat: p.coords.latitude, lng: p.coords.longitude, precisao: p.coords.accuracy })
        setSituacao('ok')
      },
      e => {
        if (!vivo.current) return
        // 1 = permissão negada; 2 = posição indisponível (sem sinal); 3 = demorou.
        setSituacao(e.code === 1 ? 'negada' : 'erro')
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    )
  }, [])

  // Quem já liberou antes não precisa da tela de explicação de novo. Quem bloqueou vê a tela de "negada" de uma vez.
  useEffect(() => {
    const permissoes = typeof navigator !== 'undefined' ? navigator.permissions : undefined
    if (!permissoes?.query) return
    let cancelado = false
    permissoes.query({ name: 'geolocation' as PermissionName }).then(r => {
      if (cancelado) return
      if (r.state === 'granted') pedir()
      else if (r.state === 'denied') setSituacao('negada')
    }).catch(() => { /* navegador sem a consulta: a tela explica e pede no toque */ })
    return () => { cancelado = true }
  }, [pedir])

  return { situacao, posicao, pedir }
}
